import { useEffect, useRef, type ReactNode } from 'react';
import { useBlocker } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { confirmAction } from './Confirm.js';

/**
 * Asks before an editor's unsaved changes are thrown away (UX review, 21 October 2026: every
 * routing editor dropped them silently on Back, Cancel or opening another item).
 *
 * Wraps the editor. Any edit inside it -- typing, a select, a switch -- marks it changed; leaving
 * by any path (Back, Cancel, the menu, another item, the browser's back button, closing the tab)
 * then asks first. Every one of those leaves through the address, which is why the editors open
 * by URL (openitem.ts): one blocker covers them all.
 *
 * A save is not a discard. While a save is in flight nothing is blocked (its onSuccess usually
 * navigates straight back to the list), and a save that succeeds marks the editor unchanged again.
 * A failed save keeps the changes, and the question.
 *
 * `readOnly` wraps the editor in a disabled fieldset instead: a person who may only look gets the
 * same form with nothing to press (the API would refuse it anyway).
 */
export function UnsavedGuard({ children, readOnly = false }: { children: ReactNode; readOnly?: boolean }) {
  const qc = useQueryClient();
  const dirty = useRef(false);

  useEffect(() => qc.getMutationCache().subscribe((e) => {
    if (e.type === 'updated' && e.action.type === 'success') dirty.current = false;
  }), [qc]);

  useBlocker({
    shouldBlockFn: async () => {
      if (!dirty.current || qc.isMutating() > 0) return false;
      const discard = await confirmAction({
        title: 'Discard your changes?',
        body: 'They have not been saved.',
        action: 'Discard', danger: true,
      });
      if (discard) dirty.current = false;
      return !discard;
    },
    enableBeforeUnload: () => dirty.current,
  });

  const mark = () => { if (!readOnly) dirty.current = true; };
  return (
    <fieldset className="arc-fieldset-plain" disabled={readOnly} onInputCapture={mark} onChangeCapture={mark}>
      {children}
    </fieldset>
  );
}
